import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { CopyField } from "../components/CopyField";
import { Icon } from "../components/icons";
import { RegionSection } from "../components/RegionSection";
import {
  Alert,
  Button,
  Card,
  Field,
  Input,
  Mono,
  PageHeader,
  buttonStyles,
  cx,
} from "../components/ui";
import { QuotaAlert, quotaRefusal, type QuotaRefusal } from "../components/Quota";
import { regionLabel } from "../lib/regions";
import { useCreateTunnel, useEgress, useRegions, useTunnels } from "../lib/queries";
import { stepBlocker } from "./tunnel/peer";

/**
 * Every private range, so most people never open the field.
 *
 * This is what the customer's endpoint routes into the tunnel, and a narrower
 * default would be a value somebody had to understand before they could
 * succeed at all.
 */
const DEFAULT_ALLOWED = "10.0.0.0/8, 172.16.0.0/12, 192.168.0.0/16";

/*
 * Three steps, named for where the work is rather than for the fields.
 *
 * A tunnel is two-sided: you generate a key on your own server, describe it
 * here, then go back and add us as a peer. One page of inputs hides that
 * entirely, which is how somebody fills it all in and then wonders why nothing
 * connects. The last step is the handover — it exists to be read, not filled.
 */
/* The three steps, by key. Their words are in `create.json` under `tunnel.steps`. */
const STEPS = ["yourEndpoint", "thePeer", "connectIt"] as const;

/**
 * The handover step, which exists to be read rather than filled.
 *
 * Its own component so it can be rendered on its own: it is the half of the
 * setup that happens on infrastructure this platform cannot see, and a wizard
 * hides it behind two clicks that a static render cannot make.
 */
export function HandoverStep({
  address,
  outboundAddress,
  regionName,
  /**
   * Null until the price is known, and then the add-on's own figure.
   *
   * Not a constant here. The add-on is repriceable from the operator console
   * and §6.5 gives customers 30 days' notice of a rise — a page holding its own
   * $5.00 would quote the old number through the notice period and past it, on
   * the one screen where somebody is deciding whether to pay it.
   */
  monthlyCents,
}: {
  address: string;
  outboundAddress: string | null;
  regionName: string | null;
  monthlyCents: number | null;
}) {
  const { t } = useTranslation("create");
  return (
    <Card>
      <div className="space-y-6 px-5 py-4">
        <div className="space-y-2">
          <p className="text-sm font-medium text-fg">{t("tunnel.addPeer")}</p>
          <p className="text-xs leading-5 text-fg-subtle">{t("tunnel.onYourEndpoint")}</p>
          <pre className="overflow-x-auto rounded-md border border-line-strong bg-canvas px-3 py-2 font-mono text-xs leading-5 text-fg">
{`[Peer]
PublicKey = <the key wg pubkey printed>
AllowedIPs = ${address.trim() || t("tunnel.placeholderAddress")}`}
          </pre>
          <p className="text-xs leading-5 text-fg-subtle">
            <Mono>AllowedIPs</Mono> {t("tunnel.allowedIps")}
          </p>
        </div>

        {outboundAddress && (
          <div className="space-y-2 border-t border-line pt-5">
            <p className="text-sm font-medium text-fg">{t("tunnel.letUsReach")}</p>
            <p className="text-xs leading-5 text-fg-subtle">
              {t("tunnel.dialOut", {
                region: regionName ? t("tunnel.fromRegion", { region: regionName }) : "",
              })}
            </p>
            <CopyField label={t("tunnel.outbound")} value={outboundAddress} />
          </div>
        )}

        <p className="border-t border-line pt-5 text-xs text-fg-subtle">
          {monthlyCents === null
            ? t("tunnel.billed")
            : t("tunnel.monthly", { amount: `$${(monthlyCents / 100).toFixed(2)}` })}{" "}
          {t("tunnel.whileItExists")}
        </p>
      </div>
    </Card>
  );
}

export function NewTunnelPage() {
  const { t } = useTranslation("create");
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id;
  const navigate = useNavigate();

  const create = useCreateTunnel(orgId ?? "");
  const regions = useRegions(orgId);
  const egress = useEgress(orgId);
  /*
   * One row, for the price in its `meta` rather than for the rows.
   *
   * The list route derives the add-on's price server-side precisely so screens
   * stop holding their own copy, and this is the screen where the number is
   * being decided on. A page is the cheapest way to ask for it — and the
   * cheapest page: the status resolver fans out over the rows returned, so a
   * limit of one is at most one engine call and, for the tenant most likely to
   * be on this page, none at all.
   */
  const priced = useTunnels(orgId, { limit: 1, offset: 0 });

  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [region, setRegion] = useState<string | null>(null);
  const [privateKey, setPrivateKey] = useState("");
  const [address, setAddress] = useState("");
  const [peerPublicKey, setPeerPublicKey] = useState("");
  const [endpoint, setEndpoint] = useState("");
  const [allowedIps, setAllowedIps] = useState(DEFAULT_ALLOWED);
  const [error, setError] = useState<string | null>(null);
  // A refused create that was the ceiling, not a mistake: rendered with the numbers and the way to ask.
  const [refusal, setRefusal] = useState<QuotaRefusal | null>(null);

  // Resolved through the suggestion when the picker is on Auto, which is its
  // default: matching the literal null found nothing, so the address that
  // prevents the most common failure was invisible unless a region was picked.
  const effectiveRegion = region ?? regions.data?.placement.suggested ?? null;
  const outbound = egress.data?.data.find((entry) => entry.region === effectiveRegion);

  const currentBlocker = stepBlocker(step, {
    name,
    endpoint,
    peerPublicKey,
    privateKey,
    address,
    allowedIps,
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setRefusal(null);
    create.mutate(
      {
        name,
        region,
        peer: {
          privateKey: privateKey.trim(),
          address: address.trim(),
          peerPublicKey: peerPublicKey.trim(),
          endpoint: endpoint.trim(),
          allowedIps: allowedIps
            .split(",")
            .map((cidr) => cidr.trim())
            .filter(Boolean),
        },
      },
      {
        /*
         * Onto the tunnel itself, not back to the list. Nothing is reachable
         * through a new tunnel yet — it has no addresses, and the peer still
         * has to be added on the customer's own endpoint — and both of those
         * live on its page. Landing on a list would ask them to find the row
         * they just made before they could finish.
         */
        onSuccess: (created) => navigate(`/tunnels/${created.data.id}`),
        onError: (err) => {
          setRefusal(quotaRefusal(err));
          setError(err.message);
        },
      },
    );
  }

  return (
    <div className="space-y-8">
      <PageHeader
        title={t("tunnel.title")}
        description={t("tunnel.description")}
      />

      <ol className="flex items-stretch border border-line">
        {STEPS.map((key, index) => {
          const state = index === step ? "current" : index < step ? "done" : "todo";
          return (
            <li key={key} className="flex-1">
              <button
                type="button"
                // Backwards only. Jumping ahead would skip the validation that
                // makes the next step answerable.
                disabled={index > step}
                onClick={() => setStep(index)}
                className={cx(
                  "flex w-full items-center gap-2 px-2.5 py-3 text-left text-xs transition-colors duration-200 ease-smooth sm:gap-2.5 sm:px-4 sm:text-sm",
                  index > 0 && "border-l border-line",
                  state === "current" && "bg-accent-soft/50 text-fg",
                  state === "done" && "text-fg-muted hover:bg-raised",
                  state === "todo" && "text-fg-subtle",
                )}
              >
                <span
                  className={cx(
                    "grid size-5 shrink-0 place-items-center text-[11px] tabular-nums sm:size-6 sm:text-xs",
                    state === "current" && "bg-accent text-white",
                    state === "done" && "bg-mint text-canvas",
                    state === "todo" && "border border-line text-fg-subtle",
                  )}
                >
                  {state === "done" ? <Icon name="check" className="size-3.5" /> : index + 1}
                </span>
                <span className="truncate">{t(`tunnel.steps.${key}`)}</span>
              </button>
            </li>
          );
        })}
      </ol>

      {refusal ? <QuotaAlert refusal={refusal} /> : error && <Alert>{error}</Alert>}

      <form onSubmit={submit} className="space-y-6">
        {step === 0 && (
          <>
            {/*
              The prerequisite, before the fields that assume it. Somebody
              without a WireGuard endpoint should find that out here rather
              than after filling in five values.
            */}
            <Card>
              <div className="space-y-2 px-5 py-4">
                <p className="text-sm font-medium text-fg">{t("tunnel.firstOnServer")}</p>
                <p className="text-xs leading-5 text-fg-subtle">{t("tunnel.generate")}</p>
                <CopyField
                  label={t("tunnel.generateTitle")}
                  value="wg genkey | tee private.key | wg pubkey"
                />
              </div>
            </Card>

            <Card>
              <div className="space-y-5 px-5 py-4">
                <Field label={t("tunnel.name")} htmlFor="t-name">
                  <Input
                    id="t-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t("tunnel.namePlaceholder")}
                  />
                </Field>
                <Field
                  label={t("tunnel.endpointAddress")}
                  htmlFor="t-ep"
                  hint={t("tunnel.endpointHint")}
                >
                  <Input
                    id="t-ep"
                    value={endpoint}
                    onChange={(e) => setEndpoint(e.target.value)}
                    placeholder={t("tunnel.endpointPlaceholder")}
                    spellCheck={false}
                  />
                </Field>
                <Field
                  label={t("tunnel.publicKey")}
                  htmlFor="t-pub"
                  hint={t("tunnel.publicKeyHint")}
                >
                  <Input
                    id="t-pub"
                    value={peerPublicKey}
                    onChange={(e) => setPeerPublicKey(e.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                  />
                </Field>
              </div>
            </Card>

            <RegionSection
              catalogue={regions.data}
              loading={regions.isPending}
              value={region}
              onChange={setRegion}
            />
          </>
        )}

        {step === 1 && (
          <Card>
            <div className="space-y-5 px-5 py-4">
              <Field
                label={t("tunnel.privateKey")}
                htmlFor="t-key"
                hint={t("tunnel.privateKeyHint")}
              >
                <Input
                  id="t-key"
                  value={privateKey}
                  onChange={(e) => setPrivateKey(e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                />
              </Field>
              <Field
                label={t("tunnel.address")}
                htmlFor="t-addr"
                hint={t("tunnel.addressHint")}
              >
                <Input
                  id="t-addr"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="10.0.0.2/32"
                  spellCheck={false}
                />
              </Field>

              {/*
                Folded, and defaulted to every private range: it is what the far
                side routes into the tunnel, and a narrower default would be a
                value somebody had to understand before they could succeed.
              */}
              <details className="group">
                <summary className="flex cursor-pointer list-none items-center gap-1.5 text-xs text-fg-muted transition-colors duration-200 ease-smooth hover:text-fg [&::-webkit-details-marker]:hidden">
                  <Icon
                    name="chevron"
                    className="size-3 transition-transform duration-200 ease-smooth group-open:rotate-90"
                  />
                  {t("tunnel.routes")}
                  <span className="text-fg-subtle group-open:hidden">{t("tunnel.allPrivate")}</span>
                </summary>
                <div className="mt-3">
                  <Field label={t("tunnel.allowed")} htmlFor="t-allowed">
                    <Input
                      id="t-allowed"
                      value={allowedIps}
                      onChange={(e) => setAllowedIps(e.target.value)}
                      spellCheck={false}
                    />
                  </Field>
                </div>
              </details>
            </div>
          </Card>
        )}

        {step === 2 && (
          <HandoverStep
            address={address}
            outboundAddress={outbound?.address ?? null}
            regionName={effectiveRegion ? (regionLabel(effectiveRegion) ?? effectiveRegion) : null}
            monthlyCents={priced.data?.meta.monthlyCents ?? null}
          />
        )}

        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* The reason, not just a disabled button that looks broken. */}
          <p className="text-xs text-fg-subtle">{currentBlocker ?? " "}</p>

          <div className="flex gap-2">
            {step === 0 ? (
              <Link to="/tunnels" className={buttonStyles("secondary")}>
                {t("shared.cancel")}
                                </Link>
            ) : (
              <Button type="button" variant="secondary" onClick={() => setStep(step - 1)}>
                {t("shared.back")}
                                </Button>
            )}

            {/*
              Both keyed, and the submit guarded on the step. A Continue/Create
              ternary on one element is one element whose type changes under the
              click that swaps it, which submits the form a step early.
            */}
            {step < STEPS.length - 1 ? (
              <Button
                key="continue"
                type="button"
                disabled={Boolean(currentBlocker)}
                onClick={() => setStep(step + 1)}
              >
                {t("shared.continue")}
                <Icon name="chevron" className="size-3.5" />
              </Button>
            ) : (
              <Button key="create" type="submit" loading={create.isPending}>
                {t("tunnel.create")}
                                </Button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
